import { FC, useEffect, useMemo, useState } from 'react';

import { useTour } from '@reactour/tour';
import { useFormikContext } from 'formik';
import { useFormWarning } from 'hooks';
import { isEqual } from 'lodash';
import { Loading } from 'pages';
import { Button } from 'ui/components/common';
import { Tabs } from 'ui/components/nav';
import { TOUR_SELECTOR } from 'ui/partials/Tour';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';

import CompanyCustomFieldsForm from './CompanyCustomFieldsForm';
import CompanyInfoForm from './CompanyInfoForm';
import { COMPANY_FORM } from './schema';

interface PROPS {
  isCreate?: boolean;
}
type TAB_VALUE = 'company-info' | 'custom-fields';

const TabItems: { label: string; value: TAB_VALUE }[] = [
  { label: 'Company Info', value: 'company-info' },
  { label: 'Custom Fields', value: 'custom-fields' },
];

const CreateCompanyForm: FC<PROPS> = ({ isCreate }) => {
  const [selectedTab, setSelectedTab] = useState<TAB_VALUE>('company-info');

  const { isOpen } = useTour();

  const renderComponent = useMemo(() => {
    switch (selectedTab) {
      case 'company-info':
        return <CompanyInfoForm />;
      case 'custom-fields':
        return <CompanyCustomFieldsForm />;
    }
  }, [selectedTab]);

  const {
    values,
    errors,
    handleSubmit,
    setValues,
    isSubmitting,
    initialValues,
  } = useFormikContext<COMPANY_FORM>();

  const { onBack } = useFormWarning({
    isDirty: !isEqual(initialValues, values),
    item: 'company',
  });

  useEffect(() => {
    if (isOpen) {
      setValues({
        name: 'Raging Bull',
      });
    }
  }, [isOpen, setValues]);

  if (!values.name && !isCreate) {
    return <Loading />;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div
        data-tut={TOUR_SELECTOR.TOUR.CONTACT.COMPANY.FORM.FORM}
        className="divide-y divide-separator-primary"
      >
        <Tabs
          items={TabItems}
          onChange={(val) => setSelectedTab(val.value as TAB_VALUE)}
          selectedTab={selectedTab}
          size={SIZE.Small}
          type="secondary"
        />
        {renderComponent}
      </div>
      <div
        className={cn(
          'flex justify-end gap-3',
          'px-2.5 pt-5 mt-5',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          onClick={onBack}
          disabled={isSubmitting}
          label="CANCEL"
          intent="tertiary"
        />
        <Button
          data-tut={TOUR_SELECTOR.TOUR.CONTACT.COMPANY.FORM.SAVE_BUTTON}
          disabled={
            isSubmitting ||
            !!Object.keys(errors).length ||
            values.data?.some((d) => !!d.error)
          }
          label="SAVE"
          type="submit"
        />
      </div>
    </form>
  );
};

export default CreateCompanyForm;
